import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import {
  getIpSets, getIpTop, buscarIp, ipToggle, getBitacora, countryName, countryFlag, fmtArDateTime,
  IpSets, IpTop, IpBuscar, IpEstado, IpAction, IpSetEntry, BitacoraEntry,
} from "../services/monitoring";

const RANGOS: { hours: number; label: string }[] = [
  { hours: 1,   label: "Última hora" },
  { hours: 24,  label: "Último día" },
  { hours: 168, label: "Última semana" },
  { hours: 336, label: "14 días (todo el log)" },
];

const ESTADO_META: Record<IpEstado, [string, string, string, string]> = {
  //             fg         bg         label                   tooltip
  habilitada:           ["#166534", "#DCFCE7", "HABILITADA",        "Excepción del panel: entra aunque su país no esté en la allowlist geo"],
  bloqueada:            ["#9A3412", "#FFEDD5", "BLOQUEADA",         "Bloqueada desde este panel (IP set webadmin2-ip-block)"],
  "geo-no-habilitado":  ["#B91C1C", "#FEE2E2", "GEO: NO HABILITADO", "Su país no está en la allowlist geo: el WAF la bloquea. Habilitar la IP la deja pasar igual"],
  "geo-bloqueado":      ["#B91C1C", "#FEE2E2", "GEO: BLOQUEADO",    "Su país está bloqueado desde Monitoreo · Países. Habilitar la IP la deja pasar igual"],
  normal:               ["#374151", "#F1F5F9", "normal",            "Sin reglas por IP: la tratan las reglas generales del WAF"],
};

const nf = new Intl.NumberFormat("es-AR");

// "2026-09-03 18:55:29.203" (UTC, Insights) → hora AR
function fmtTs(s?: string | null): string {
  if (!s) return "—";
  const iso = s.replace(" ", "T").replace(/(\.\d+)?$/, "") + "Z";
  return fmtArDateTime(iso);
}

function EstadoChip({ estado }: { estado: IpEstado }) {
  const [fg, bg, label, tip] = ESTADO_META[estado] ?? ESTADO_META.normal;
  return (
    <Tooltip title={tip}>
      <Chip size="small" label={label} sx={{ fontWeight: 700, fontSize: 11, height: 22, color: fg, bgcolor: bg }} />
    </Tooltip>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 140, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
    </Box>
  );
}

type Confirm = { ip: string; action: IpAction; nota: string };

function SetTable({ title, rows, color, bg, removeAction, busy, onRemove, onBuscar, emptyText }: {
  title: string; rows: IpSetEntry[]; color: string; bg: string; removeAction: IpAction;
  busy: boolean; onRemove: (c: Confirm) => void; onBuscar: (ip: string) => void; emptyText: string;
}) {
  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: "hidden" }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: bg }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color }}>{title} ({rows.length})</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>IP / rango</TableCell>
            <TableCell>Motivo</TableCell>
            <TableCell>Quién</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Cuándo</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2, color: "text.secondary" }}>{emptyText}</TableCell></TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.cidr} hover>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>{r.cidr}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{r.texto?.replace(/^IP \S+ /, "") ?? "—"}</TableCell>
              <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{r.por ?? "—"}</TableCell>
              <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{r.ts ? fmtArDateTime(r.ts) : "—"}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title="Buscar en los logs del WAF">
                  <span><IconButton size="small" onClick={() => onBuscar(r.cidr.replace(/\/32$/, ""))}><TravelExploreIcon fontSize="small" /></IconButton></span>
                </Tooltip>
                <Tooltip title={removeAction === "unallow" ? "Quitar de las habilitadas" : "Desbloquear"}>
                  <span>
                    <IconButton size="small" disabled={busy} color={removeAction === "unallow" ? "warning" : "success"}
                      onClick={() => onRemove({ ip: r.cidr, action: removeAction, nota: "" })}>
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function AccionesIp({ ip, estado, busy, onConfirm }: {
  ip: string; estado: IpEstado; busy: boolean; onConfirm: (c: Confirm) => void;
}) {
  const puedeHabilitar = estado !== "habilitada";
  const puedeBloquear  = estado !== "bloqueada";
  return (
    <>
      {estado === "habilitada" ? (
        <Tooltip title="Quitar de las habilitadas">
          <span><IconButton size="small" color="warning" disabled={busy}
            onClick={() => onConfirm({ ip, action: "unallow", nota: "" })}><LockOpenIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      ) : puedeHabilitar && (
        <Tooltip title="Habilitar esta IP (pasa aunque su país esté bloqueado)">
          <span><IconButton size="small" color="success" disabled={busy}
            onClick={() => onConfirm({ ip, action: "allow", nota: "" })}><LockOpenIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      )}
      {estado === "bloqueada" ? (
        <Tooltip title="Desbloquear esta IP">
          <span><IconButton size="small" color="success" disabled={busy}
            onClick={() => onConfirm({ ip, action: "unblock", nota: "" })}><LockOpenIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      ) : puedeBloquear && (
        <Tooltip title="Bloquear esta IP en el WAF (afecta tráfico real)">
          <span><IconButton size="small" color="error" disabled={busy}
            onClick={() => onConfirm({ ip, action: "block", nota: "" })}><BlockIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      )}
    </>
  );
}

export default function MonitoreoIps() {
  const [sets,    setSets]    = useState<IpSets | null>(null);
  const [hours,   setHours]   = useState(24);
  const [top,     setTop]     = useState<IpTop | null>(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [ok,      setOk]      = useState<string | null>(null);

  const [q,        setQ]        = useState("");
  const [buscando, setBuscando] = useState(false);
  const [result,   setResult]   = useState<IpBuscar | null>(null);

  const [bitacora, setBitacora] = useState<BitacoraEntry[]>([]);
  const [busy,     setBusy]     = useState(false);
  const [confirm,  setConfirm]  = useState<Confirm | null>(null);

  async function loadSets() {
    try { setSets(await getIpSets()); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
  }
  async function loadTop(h = hours) {
    setLoading(true);
    try { setTop(await getIpTop(h)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }
  async function loadBitacora() {
    try { setBitacora(await getBitacora(undefined, "ip")); }
    catch { /* secundario */ }
  }
  // Desde el vigía (Países › Vigía › "Bloquear en IPs") llega ?ip=…: se busca sola.
  const [sp] = useSearchParams();
  useEffect(() => { loadSets(); loadBitacora(); const ip = sp.get("ip"); if (ip) onBuscar(ip); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadTop(hours); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [hours]);

  async function onBuscar(ip = q) {
    const v = ip.trim();
    if (!v) return;
    setQ(v); setBuscando(true); setErr(null); setResult(null);
    try { setResult(await buscarIp(v, 336)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBuscando(false); }
  }

  async function onToggle() {
    if (!confirm) return;
    const { ip, action, nota } = confirm;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await ipToggle(ip, action, nota);
      const msg: Record<IpAction, string> = {
        allow:   `${r.cidr} HABILITADA: entra aunque su país esté bloqueado.`,
        unallow: `${r.cidr} quitada de las habilitadas.`,
        block:   `${r.cidr} BLOQUEADA a nivel WAF.`,
        unblock: `${r.cidr} desbloqueada.`,
      };
      setOk(r.changed ? msg[action] : `${r.cidr}: sin cambios (ya estaba así).`);
      setConfirm(null);
      await Promise.all([loadSets(), loadBitacora()]);
      // Refrescar estados en la tabla y en el resultado de búsqueda.
      loadTop(hours);
      if (result && (result.ip === ip.replace(/\/32$/, "") || result.cidr === ip)) onBuscar(result.ip);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  const rangoLabel = RANGOS.find((r) => r.hours === hours)?.label ?? `${hours} h`;
  const accionLabel: Record<IpAction, string> = {
    allow: "Habilitar IP", unallow: "Quitar de habilitadas", block: "Bloquear IP", unblock: "Desbloquear IP",
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Monitoreo · IPs</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Qué IPs llegan a los servicios públicos según los logs del WAF (14 días de retención), y las
        reglas por IP del panel: <b>habilitar</b> una IP la deja entrar aunque su país no esté en la
        allowlist geo (o esté bloqueado desde Países); <b>bloquear</b> la corta siempre. Ambas afectan
        tráfico REAL en producción y quedan en la bitácora con motivo, quién y cuándo.
      </Typography>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      {/* Buscador */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Buscar IP" value={q} placeholder="147.79.84.224"
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onBuscar()}
            sx={{ minWidth: 260 }} inputProps={{ style: { fontFamily: "monospace" } }} />
          <Button variant="contained" startIcon={buscando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={() => onBuscar()} disabled={buscando || !q.trim()}>Buscar</Button>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Busca en los 14 días de logs del WAF: país, servicios, URIs, acción y regla que la trató.
          </Typography>
        </Stack>

        {result && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 17 }}>{result.ip}</Typography>
              {result.country && (
                <Chip size="small" label={`${countryFlag(result.country)} ${countryName(result.country)} (${result.country})`} />
              )}
              <EstadoChip estado={result.estado} />
              {result.cidr && result.cidr !== `${result.ip}/32` && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>vía rango {result.cidr}</Typography>
              )}
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {nf.format(result.total)} requests · {nf.format(result.blocked)} bloqueadas · {fmtArDateTime(result.from)} → {fmtArDateTime(result.to)}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <AccionesIp ip={result.cidr ?? result.ip} estado={result.estado} busy={busy} onConfirm={setConfirm} />
            </Stack>
            {Object.keys(result.headers ?? {}).length > 0 && (
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                Headers de muestra: {Object.entries(result.headers).map(([k, v]) => `${k}: ${v}`).join(" · ")}
              </Typography>
            )}
            {result.rows.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Sin requests de esta IP en los últimos 14 días.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow>
                    <TableCell>Host</TableCell><TableCell>URI</TableCell><TableCell>Acción</TableCell>
                    <TableCell>Regla</TableCell><TableCell align="right">Requests</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>Primera</TableCell><TableCell sx={{ whiteSpace: "nowrap" }}>Última</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {result.rows.map((r, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{r.host}</TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Tooltip title={r.uri ?? ""}><span>{r.uri}</span></Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: r.action === "BLOCK" ? "#B91C1C" : "#166534" }}>{r.action}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>{r.rule}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{nf.format(r.requests)}</TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtTs(r.first)}</TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtTs(r.last)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Sets del panel */}
      {sets && (
        <>
          {!sets.rules_installed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Las reglas por IP todavía no están instaladas en el WAF ({sets.acl}); se crean solas con la
              primera IP que habilites o bloquees.
            </Alert>
          )}
          <SetTable title="IPs habilitadas (excepción a la allowlist geo)" rows={sets.allow}
            color="#166534" bg="#F0FDF4" removeAction="unallow" busy={busy}
            onRemove={setConfirm} onBuscar={onBuscar}
            emptyText="Ninguna. Habilitá una IP cuando un cliente o integración opere desde un país no habilitado." />
          <SetTable title="IPs bloqueadas desde este panel" rows={sets.block}
            color="#9A3412" bg="#FFF7ED" removeAction="unblock" busy={busy}
            onRemove={setConfirm} onBuscar={onBuscar}
            emptyText="Ninguna." />
        </>
      )}

      {/* Top IPs */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, mt: 3 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Top IPs</Typography>
        <TextField size="small" select label="Rango" value={hours}
          onChange={(e) => setHours(Number(e.target.value))} sx={{ minWidth: 200 }}>
          {RANGOS.map((r) => <MenuItem key={r.hours} value={r.hours}>{r.label}</MenuItem>)}
        </TextField>
        {top && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {fmtArDateTime(top.from)} → {fmtArDateTime(top.to)} (hora AR)
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refrescar (el backend cachea 5 min)">
          <span><IconButton onClick={() => { loadTop(); loadSets(); loadBitacora(); }} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>

      {top && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <StatTile label={`Requests (${rangoLabel.toLowerCase()})`} value={nf.format(top.total)} />
          <StatTile label="Bloqueadas" value={nf.format(top.blocked)} color={top.blocked ? "#B91C1C" : "#166534"} />
          <StatTile label="IPs distintas" value={nf.format(top.ips)} />
          <StatTile label="Habilitadas / bloqueadas (panel)" value={`${sets?.allow.length ?? 0} / ${sets?.block.length ?? 0}`}
            color="#6B7280" />
        </Stack>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>IP</TableCell>
              <TableCell>País</TableCell>
              <TableCell align="right">Requests</TableCell>
              <TableCell align="right">Bloqueadas</TableCell>
              <TableCell align="right">URIs</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Último acceso</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !top && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {top?.rows.map((r) => {
              const marcada = r.estado === "habilitada" || r.estado === "bloqueada";
              return (
                <TableRow key={`${r.ip}#${r.country}`} hover
                  sx={marcada ? { bgcolor: r.estado === "habilitada" ? "#F0FDF4" : "#FFF7ED" } : undefined}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>{r.ip}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {countryFlag(r.country)} {countryName(r.country)}
                    <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 0.75 }}>{r.country}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(r.total)}</TableCell>
                  <TableCell align="right">
                    {r.blocked > 0 ? (
                      <Chip size="small" label={nf.format(r.blocked)}
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
                    ) : <Typography variant="caption" sx={{ color: "text.secondary" }}>0</Typography>}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>{nf.format(r.uris)}</TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtTs(r.last)}</TableCell>
                  <TableCell><EstadoChip estado={r.estado} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Ver detalle en los logs del WAF">
                      <span><IconButton size="small" onClick={() => onBuscar(r.ip)}><TravelExploreIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <AccionesIp ip={r.cidr ?? r.ip} estado={r.estado} busy={busy} onConfirm={setConfirm} />
                  </TableCell>
                </TableRow>
              );
            })}
            {top && top.rows.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin tráfico en el rango.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bitácora de IPs */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Bitácora de IPs</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Fecha</TableCell><TableCell>IP</TableCell>
            <TableCell>Quién</TableCell><TableCell>Detalle</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {bitacora.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 2, color: "text.secondary" }}>Sin movimientos todavía.</TableCell></TableRow>
            )}
            {bitacora.map((e) => (
              <TableRow key={`${e.ts}#${e.resultado?.cidr ?? ""}`} hover>
                <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtArDateTime(e.ts)}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{e.resultado?.cidr ?? "—"}</TableCell>
                <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.por}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{e.texto}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{confirm ? accionLabel[confirm.action] : ""}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {confirm?.action === "block" && (
              <>Vas a <b>bloquear {confirm.ip}</b> en el WAF del ALB público: todo su tráfico se corta,
                en producción, ahora. Si es un cliente o integración legítima, deja de funcionar.</>
            )}
            {confirm?.action === "unblock" && (
              <>Vas a <b>desbloquear {confirm.ip}</b>: su tráfico vuelve a entrar normalmente (sujeto a las reglas geo).</>
            )}
            {confirm?.action === "allow" && (
              <>Vas a <b>habilitar {confirm.ip}</b>: entra aunque su país no esté en la allowlist geo o esté
                bloqueado desde Países. El resto de las reglas del WAF (managed, SQLi) se siguen aplicando.</>
            )}
            {confirm?.action === "unallow" && (
              <>Vas a <b>quitar {confirm.ip} de las habilitadas</b>: vuelve a regirse por las reglas geo de su país.</>
            )}
            {" "}Queda asentado en la bitácora.
          </DialogContentText>
          <TextField autoFocus fullWidth size="small" label="Motivo (opcional, queda en la bitácora)"
            value={confirm?.nota ?? ""} onChange={(e) => setConfirm((c) => c ? { ...c, nota: e.target.value } : c)}
            placeholder="Ej: integración del colector X hosteada en Hostinger (AU)" inputProps={{ maxLength: 300 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={onToggle} disabled={busy} variant="contained"
            color={confirm?.action === "block" ? "error" : confirm?.action === "unallow" ? "warning" : "success"}>
            {busy ? "Aplicando…" : confirm ? accionLabel[confirm.action] : ""}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
